import type { Session } from '@colplat/core-client';
import { fieldKey, type PresenceUser, type Uuid } from '@colplat/protocol';
import { escapeHtml } from './dom.js';
import { describeFieldState } from './state-visuals.js';
import { presenceDotHtml } from './presence.js';
import { historyButtonHtml, historyMarkup } from './fieldHistory.js';

/**
 * A compact text field bound to a `core-client` `Session`. Ported from the
 * reference demo's `fieldBlock` (`docs/reference/collab-field-controls.html`)
 * — border carries state, label floats into a notch, history sits in the
 * tool slot. Re-renders are targeted to this one field's container, never a
 * full-page blow-away (ADR-0019), and `repaint()` restores focus/cursor so a
 * remote update elsewhere on the page never disturbs an active edit.
 */

export interface CollabFieldOptions {
  session: Session;
  entity: string;
  recordId: Uuid;
  field: string;
  label: string;
  multiline?: boolean;
}

export interface MountedField {
  /** Re-render without changing anything — call after an external state change (e.g. record opened). */
  refresh(): void;
  destroy(): void;
}

export function mountCollabField(container: HTMLElement, opts: CollabFieldOptions): MountedField {
  const key = fieldKey(opts.entity, opts.recordId, opts.field);
  let historyOpen = false;
  // `repaint()` replaces the focused `<input>` with a brand-new DOM node
  // (a full `innerHTML` re-render, ADR-0019) and then calls `.focus()` on it
  // to restore the user's place — but removing the old node synchronously
  // fires a real `blur` on it, and focusing the new one fires a real
  // `focus`, both indistinguishable from the user's own actions unless
  // something says otherwise. This flag is that something: set for the
  // synchronous duration of `repaint()`'s own render+refocus, so those two
  // self-inflicted events don't trigger a real claim or a real commit.
  // Without it: focus -> claim -> (session notifies) -> repaint -> its own
  // focus() -> onFocus again, forever.
  let repainting = false;

  function resolveUser(id: Uuid): PresenceUser | undefined {
    return opts.session.presence(opts.entity, opts.recordId).find((u) => u.id === id);
  }

  function markup(): string {
    const read = opts.session.field(opts.entity, opts.recordId, opts.field);
    const review = opts.session.pendingReviews(opts.entity, opts.recordId).find((r) => r.key === key);
    const visual = describeFieldState(read.state, resolveUser);
    const value = read.value && read.value.t !== 'null' ? String(read.value.v) : '';
    const roAttrs = visual.readOnly ? 'readonly aria-readonly="true"' : '';
    const ariaLabel = `${escapeHtml(opts.label)}${visual.aria ? `. ${escapeHtml(visual.aria)}` : ''}`;

    const holderColor = visual.holder ? ` style="--user-color:${escapeHtml(visual.holder.colorHex)}"` : '';
    const action = visual.action
      ? `<button type="button" class="btn btn-outline-warning cf-takeover" data-take-over>${visual.action === 'takeBack' ? 'Take back' : 'Take over'}</button>`
      : '';
    /*
     * Yours, held — SPEC §6.1's "taken off you". Outside the control, not in
     * it: the control is whoever holds the field, and repainting the draft
     * away is the one thing that must not happen (invariant #5). Stays after
     * Take back, until Keep mine writes it or the field is written anyway.
     */
    const held = read.held && read.held.t !== 'null' ? String(read.held.v) : read.held ? '' : undefined;
    const heldStrip = held === undefined ? '' : `<div class="cf-f__held">
        <span aria-hidden="true">⚠</span><b>yours</b>
        ${visual.holder ? `<span class="cf-f__heldwho">${presenceDotHtml(visual.holder, 12)} ${escapeHtml(visual.holder.name)} has it</span>` : ''}
        <span class="cf-f__heldv" title="${escapeHtml(held)}">${held === '' ? '<em class="text-secondary">empty</em>' : escapeHtml(held)}</span>
        <button type="button" class="btn btn-warning" data-keep-mine>Keep mine</button>
      </div>`;

    const control = opts.multiline
      ? `<textarea class="form-control" rows="2" placeholder=" " data-field-input aria-label="${ariaLabel}" ${roAttrs}>${escapeHtml(value)}</textarea>`
      : `<input class="form-control" type="text" placeholder=" " value="${escapeHtml(value)}" data-field-input aria-label="${ariaLabel}" ${roAttrs}>`;

    const historyPanel = historyOpen
      ? `<div class="cf-hist show" data-history-panel>${historyMarkup(opts.label, read.history, review, resolveUser)}</div>`
      : '<div class="cf-hist" data-history-panel></div>';

    return `<div class="cf-f ${visual.cls}${action ? ' has-action' : ''}" data-key="${key}"${holderColor}>
      <div class="cf-f__box">
        ${control}
        <label class="cf-f__label">${escapeHtml(opts.label)}</label>
        <span class="cf-f__state" role="status">${visual.chipHtml}</span>
        <span class="cf-f__tools">${action}${historyButtonHtml(read.history.length, opts.label)}</span>
      </div>
      ${heldStrip}
      ${historyPanel}
    </div>`;
  }

  function wire(): void {
    const input = container.querySelector<HTMLInputElement | HTMLTextAreaElement>('[data-field-input]');
    // A DOM listener is typed `(e) => void`, and both of these are async:
    // handing the promise straight to `addEventListener` drops any rejection
    // (a lease that could not be taken, a release that failed) on the floor.
    // `void` is the same idiom the views use for this.
    input?.addEventListener('focus', () => { void onFocus(); });
    input?.addEventListener('blur', (event) => { void onBlur(event); });
    input?.addEventListener('input', onInput);
    container.querySelector<HTMLButtonElement>('[data-history-toggle]')?.addEventListener('click', () => {
      historyOpen = !historyOpen;
      render();
    });
    container.querySelector<HTMLButtonElement>('[data-take-over]')?.addEventListener('click', () => {
      void opts.session.takeOver(opts.entity, opts.recordId, opts.field).then((outcome) => {
        repaint();
        // Straight into the field it was taken for.
        if (outcome.ok) container.querySelector<HTMLInputElement | HTMLTextAreaElement>('[data-field-input]')?.focus();
      });
    });
    container.querySelector<HTMLButtonElement>('[data-keep-mine]')?.addEventListener('click', () => {
      void opts.session.keepMine(opts.entity, opts.recordId, opts.field).then(repaint);
    });
    container.querySelectorAll<HTMLButtonElement>('[data-review-pick]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const keep = btn.dataset['reviewPick'] as 'mine' | 'theirs';
        void opts.session.resolveReview(key, keep).then(repaint);
      });
    });
  }

  function render(): void {
    container.innerHTML = markup();
    wire();
  }

  async function onFocus(): Promise<void> {
    if (repainting) return; // our own programmatic refocus, not a real user focus
    const read = opts.session.field(opts.entity, opts.recordId, opts.field);
    const alreadyOwned = read.state.kind === 'mine' || read.state.kind === 'claiming';
    // Held by somebody else: the way in is Take over, never a silent claim.
    if (read.state.kind === 'system' || read.state.kind === 'locked' || read.state.kind === 'takenOffYou' || alreadyOwned) return;
    await opts.session.claim(opts.entity, opts.recordId, opts.field);
    repaint();
  }

  function onInput(event: Event): void {
    const el = event.target as HTMLInputElement | HTMLTextAreaElement;
    opts.session.preview(opts.entity, opts.recordId, opts.field, { t: 'string', v: el.value });
  }

  async function onBlur(event: Event): Promise<void> {
    if (repainting) return; // the old input node was just removed by our own re-render, not a real blur
    // Switching apps (alt-tab, clicking another window) fires `blur` on the
    // focused element in most browsers even though the user never intended
    // to leave this field — e.g. copying a value in from elsewhere before
    // pasting it here. `document.hasFocus()` distinguishes that from a real
    // "done with this field" blur: it stays true while focus moves to
    // ANOTHER element within this same page, and only goes false when the
    // browser window itself loses OS-level focus. Treat the latter as "still
    // editing" — don't commit, don't release; the lease keeps renewing via
    // its own visibility-based heartbeat regardless.
    if (!document.hasFocus()) return;
    const el = event.target as HTMLInputElement | HTMLTextAreaElement;
    const read = opts.session.field(opts.entity, opts.recordId, opts.field);
    if (read.state.kind !== 'mine' && read.state.kind !== 'claiming') return;
    await opts.session.commitAndRelease(opts.entity, opts.recordId, opts.field, { t: 'string', v: el.value });
    repaint();
  }

  /**
   * Full re-render (ui-web has no partial-patch machinery), then restore
   * focus, cursor — and the half-typed value, which is the part that was
   * missing.
   *
   * `markup()` reads the field's value from the session, so a re-render
   * writes the *stored* value into the box. That is right for every field
   * except the one this person is in the middle of typing into, and this
   * function runs on every `session.subscribe` notification — which includes
   * `recordEvent` for records that have nothing to do with this field. So
   * somebody else saving a transaction in another window threw away whatever
   * you had typed into an account's name and put the old name back, with
   * your cursor obediently restored to where it had been.
   *
   * Invariant #5 is explicit about this: never repaint a value under an
   * active cursor; a remote change to a field the local user is editing is
   * queued and applied on release. The queueing half was built (`Session`'s
   * `queuedRemote`) and the repaint went around it, because it never asked
   * what was in the box before replacing it.
   *
   * Only a field this person actually holds is preserved. One somebody else
   * holds is read-only and has no draft in it, and must keep showing their
   * changes as they arrive.
   */
  function repaint(): void {
    const input = container.querySelector<HTMLInputElement | HTMLTextAreaElement>('[data-field-input]');
    const hadFocus = document.activeElement === input;
    const selectionStart = input?.selectionStart ?? null;
    const selectionEnd = input?.selectionEnd ?? null;
    /*
     * Whatever is in the box, if the box is one the user can type in.
     *
     * This asked whether the field was `mine` or `claiming`, which is the
     * same thing a moment later but not yet: claiming a lease takes a round
     * trip, and `repaint` runs several times during it. A character typed in
     * that window was read against a state still saying `idle`, treated as
     * not-a-draft, and replaced by the stored value.
     *
     * A readonly box is the one case to leave alone — it is somebody else's
     * field or a system one, nothing of the user's is in it, and their
     * changes must keep arriving.
     */
    const draft = input && !input.readOnly && hadFocus ? input.value : undefined;
    repainting = true;
    try {
      render();
      const next = container.querySelector<HTMLInputElement | HTMLTextAreaElement>('[data-field-input]');
      if (next && draft !== undefined && next.value !== draft) next.value = draft;
      if (hadFocus && next) {
        next.focus();
        if (selectionStart !== null) {
          try {
            next.setSelectionRange(selectionStart, selectionEnd ?? selectionStart);
          } catch { /* not a text-selectable input */ }
        }
      }
    } finally {
      repainting = false;
    }
  }

  const unsubscribe = opts.session.subscribe(() => repaint());
  render();
  return { refresh: () => repaint(), destroy: unsubscribe };
}
